import {
    PendingEarningsTicker,
    type PendingEarningsTickerProps
} from "@shared/widgets/yaas/PendingEarningsTicker";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";

import { useDistributions } from "@/hooks/yaas";

// Tick cadence — matches the IndicationUnit pulse's `pulseIntervalMs`
// at the call site so the green flash and the digit shift fire on the
// same N×interval cadence from mount.
const TICK_INTERVAL_MS = 1500;

interface RetailPendingEarningsTickerProps {
    walletAddress: string;
    totalYieldEarned: number;
    positionValue: BigDecimal | string | number;
    averageAPY: string | null;
    isLoaded: boolean;
    hasActivePosition: boolean;
    color?: string;
    prefix?: string;
    earliestPositionCreatedAt?: string | null;
    /** Text variant — controls font/size. Default: `number`. Pass
     *  `hero` to render as the page hero number. */
    variant?: PendingEarningsTickerProps["variant"];
    /** Optional base amount added on top of the calculated pending
     *  value. Use to surface total cumulative earnings (past
     *  distributions + live pending) rather than just pending. */
    baseAmount?: BigDecimal | string | number;
    /** Tailwind classes forwarded to the wrapping Text element. Use
     *  for typography overrides like `font-numeric tabular-nums`
     *  when rendering at hero size before a real number-hero
     *  variant ships. */
    className?: string;
}

// Real-time pending-earnings ticker — apps/main data wrapper. Fetches
// the most recent distribution to compute a baseline (or falls back to
// the position activation date), then ramps per-second accrual at 1.5s
// intervals. The presentational render (dual NumberFlow, sub-cent
// precision, the `::part(symbol)` styling) lives in the shared
// `PendingEarningsTicker`; this wrapper owns the SDK + accrual clock
// and feeds it the current value. Public API is unchanged from before
// the shared hoist — both callers (PortfolioSummary, RetailYaasHeroCard)
// keep working as-is.
export function RetailPendingEarningsTicker({
    walletAddress,
    totalYieldEarned,
    positionValue,
    averageAPY,
    isLoaded,
    hasActivePosition,
    color = "rgb(var(--color-foreground-base))",
    prefix = "$",
    earliestPositionCreatedAt,
    variant = "number",
    baseAmount = 0,
    className
}: RetailPendingEarningsTickerProps): React.ReactElement {
    const [displayValue, setDisplayValue] = useState(() => new BigDecimal(0));

    const { data: distributionsData } = useDistributions({
        address: walletAddress,
        limit: 1
    });

    const getApy = useCallback((): BigDecimal => {
        const match = averageAPY?.trim().match(/^\d+(?:\.\d+)?/);
        return new BigDecimal(match?.[0] ?? "0");
    }, [averageAPY]);

    const calculateInitialPending = useCallback((): BigDecimal => {
        const yaasApy = getApy();
        if (
            !new BigDecimal(positionValue).gt(0) ||
            !yaasApy.gt(0) ||
            !hasActivePosition
        ) {
            return new BigDecimal(0);
        }

        const lastDistribution = distributionsData?.data?.[0];
        const referenceDate =
            lastDistribution?.distributionDate ?? earliestPositionCreatedAt;

        if (!referenceDate) return new BigDecimal(0);

        const startDate = new Date(referenceDate);
        // Clamp to zero — a freshly-created position can read as a few
        // ms in the future if the server and client clocks have drifted
        // (or the position was created on a different timezone). Without
        // this, elapsed time is slightly negative → accrual goes negative.
        const elapsedMs = Math.max(0, Date.now() - startDate.getTime());
        if (!Number.isFinite(elapsedMs)) return new BigDecimal(0);
        return new BigDecimal(positionValue)
            .multiply(yaasApy)
            .multiply(elapsedMs)
            .divide(100 * 365 * 24 * 60 * 60 * 1000);
    }, [
        distributionsData,
        positionValue,
        getApy,
        hasActivePosition,
        earliestPositionCreatedAt
    ]);

    useEffect(() => {
        const initial = calculateInitialPending().add(baseAmount);
        setDisplayValue(initial);

        const yaasApy = getApy();
        if (
            !new BigDecimal(positionValue).gt(0) ||
            !yaasApy.gt(0) ||
            !hasActivePosition
        )
            return;

        const incrementPerInterval = new BigDecimal(positionValue)
            .multiply(yaasApy)
            .multiply(TICK_INTERVAL_MS)
            .divide(100 * 365 * 24 * 60 * 60 * 1000);

        let accumulated = initial;
        const interval = setInterval(() => {
            accumulated = accumulated.add(incrementPerInterval);
            setDisplayValue(accumulated);
        }, TICK_INTERVAL_MS);

        return (): void => clearInterval(interval);
    }, [
        positionValue,
        getApy,
        hasActivePosition,
        totalYieldEarned,
        calculateInitialPending,
        baseAmount
    ]);

    return (
        <PendingEarningsTicker
            value={displayValue.toNumber()}
            isLoaded={isLoaded}
            color={color}
            prefix={prefix}
            variant={variant}
            className={className}
        />
    );
}
